/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { useEffect, useRef } from "react"
import { notification } from "@duxton-web/ui"
import { CheckmarkUniversal } from "@duxton-web/icons"
import _uniqueId from "lodash/uniqueId"
import _toString from "lodash/toString"

import { menuEditorTabKeys } from "constants/menu"
import { BulkUploadTask } from "types/delv-platform-api/bulkUploadTask"
import { useTranslations } from "common/utils/i18n"
import { useTranslateSingularAndPlural } from "hooks/useTranslateSingularAndPlural"
import { useHistory, useLocation } from "react-router"

type PropTypes = {
  // to show notification only once when uploading successfully
  // visible prop should be the combination of isPolling (fetching history endpoint every 20s if there's ongoing bulk upload) and the latest response is successfully
  visible: boolean
  completedBulkUploadTask?: BulkUploadTask
  onCloseCallback: () => void
}

const notificationMessageWrapCss = css`
  display: flex;
  align-items: center;
`

const leftColWrapCss = css`
  display: flex;
  align-items: center;
`

const successIconWrapCss = css`
  background-color: #33c072;
  width: 36px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  .duxicon {
    color: #ffffff;
    font-size: 28px;
  }
`

const textCss = css`
  font-size: 16px;
  font-weight: 500;
`

const UploadSuccessNoError = ({
  visible,
  completedBulkUploadTask,
  onCloseCallback,
}: PropTypes) => {
  const location = useLocation()
  const history = useHistory()
  const notificationID = useRef<string>()
  const totalSucceededRow = completedBulkUploadTask?.successRowCount || 0

  const [viewActionText, successNoItemCountNotiText] = useTranslations([
    "bulkUpload.notifications.successWithItemCount.viewAction",
    "bulkUpload.notifications.successNoItemCount.title",
  ])
  const successNotiText = useTranslateSingularAndPlural(
    "bulkUpload.notifications.successWithItemCount.title",
    totalSucceededRow,
    { x: _toString(totalSucceededRow) },
  )

  const onClickView = () => {
    if (notificationID.current) {
      // will call notification.onClose
      notification.destroy(notificationID.current)
    }

    /**
     * if in inventory bulk upload tab -> redirect to items tab
     * if in menu bulk upload tab -> redirect to menu overview
     */
    const itemTabPath = location.pathname
      .replace(menuEditorTabKeys.bulkUploadInventory, menuEditorTabKeys.items)
      .replace(menuEditorTabKeys.bulkUploadMenu, menuEditorTabKeys.menuOverview)

    history.push(itemTabPath)
  }

  useEffect(() => {
    // only show 1 notification once
    if (visible && !notificationID.current) {
      notificationID.current = _uniqueId("upload_succes_no_error")
      notification.open({
        key: notificationID.current,
        closeIcon: false,
        placement: "topRight",
        duration: 30, // 30 seconds
        message: (
          <div css={notificationMessageWrapCss}>
            <div css={leftColWrapCss}>
              <div css={successIconWrapCss}>
                <CheckmarkUniversal />
              </div>
              <div
                css={css`
                  ${textCss};
                  color: #000000;
                  margin-left: 12px;
                  flex: 1;
                  padding-right: 20px;
                `}
              >
                {totalSucceededRow
                  ? successNotiText
                  : successNoItemCountNotiText}
              </div>
            </div>
            <div
              css={css`
                ${textCss};
                color: #136fd8;
                cursor: pointer;
              `}
              onClick={onClickView}
            >
              {viewActionText}
            </div>
          </div>
        ),
        onClose: () => {
          notificationID.current = ""
          onCloseCallback()
        },
        className: "notification-success",
      })
    }
  }, [visible, onCloseCallback])

  return null
}

export default UploadSuccessNoError
